import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users, RefreshCw, Eye, Link2, Fingerprint, Camera, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const BiometricUnmappedTable = ({ loading, filteredUnmappedData, viewItemDetails, openMappingModal }) => {
  const { t, i18n } = useTranslation();
  return (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Users className="h-5 w-5" />
        {t('biometricComp.unmappedTitle', { count: filteredUnmappedData.length })}
      </CardTitle>
      <CardDescription>{t('biometricComp.unmappedDesc')}</CardDescription>
    </CardHeader>
    <CardContent>
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="h-6 w-6 animate-spin mr-2" />
          <span>{t('biometricComp.loadingData')}</span>
        </div>
      ) : filteredUnmappedData.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('biometricComp.colRegNum')}</TableHead>
              <TableHead>{t('biometricComp.colBio')}</TableHead>
              <TableHead>{t('biometricComp.colPhotos')}</TableHead>
              <TableHead>{t('biometricComp.colTerminals')}</TableHead>
              <TableHead>{t('biometricComp.colLastSync')}</TableHead>
              <TableHead className="text-right">{t('biometricComp.colActions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUnmappedData.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  <div className="font-mono font-medium">{item.registration_number}</div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="flex items-center gap-1 w-fit">
                    <Fingerprint className="h-3 w-3" />
                    {item.biometric_count}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="flex items-center gap-1 w-fit">
                    <Camera className="h-3 w-3" />
                    {item.photo_count}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {item.terminals.map((terminal, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">{terminal}</Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-muted-foreground">
                    {item.last_synced ? new Date(item.last_synced).toLocaleString(i18n.language) : t('biometricComp.never')}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <Button variant="outline" size="sm" onClick={() => viewItemDetails(item)} title={t('biometricComp.viewDetails')}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => openMappingModal(item.registration_number)}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Link2 className="h-4 w-4 mr-2" />
                      {t('biometricComp.mapBtn')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="text-center py-8">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">{t('biometricComp.allMappedTitle')}</h3>
          <p className="text-muted-foreground">{t('biometricComp.allMappedDesc')}</p>
        </div>
      )}
    </CardContent>
  </Card>
  );
};

export default BiometricUnmappedTable;
